import { from, of } from 'rxjs';
import { catchError, filter, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { notifyPricesRecalculated } from '../../../common/signalr/signalrActions';
import { RootEpic } from '../../../store';
import {
    createEpicFromAsyncAction,
    createEpicFromAsyncActionEmptyRequest,
    includes,
    startsWith
} from '../../../utils';
import { pricePrgError } from '../../ui/redux/uiSlices';
import {
    calculateCalculation,
    createCalculation,
    createCost,
    createCostGroup,
    deleteCost,
    deleteCostGroup,
    fetchCalculation,
    fetchCalculations,
    fetchCostTypes,
    fetchPricelistPeriods,
    fetchRounders,
    recalculatePrices,
    reorderCost,
    searchProduct,
    updateCalculation,
    updateCost,
    updateCostGroup
} from './calculationSlices';

const fetchCalculation$ = createEpicFromAsyncAction(
    fetchCalculation,
    (payload, { calcClient }) => calcClient.get(payload),
    undefined,
    (_, err) => {
        if (err?.status !== 404) {
            return pricePrgError(err);
        }

        return null;
    }
);

const fetchCostTypes$ = createEpicFromAsyncActionEmptyRequest(fetchCostTypes, async ({ calcClient }) => {
    const costTypes = await calcClient.getCostTypes();
    return costTypes;
});

const createCalculation$ = createEpicFromAsyncAction(createCalculation, (payload, { calcClient }) =>
    calcClient.create(payload.pricelistId.toString(), payload)
);

const createCostGroup$ = createEpicFromAsyncAction(createCostGroup, (payload, { costGroupClient }) =>
    costGroupClient.createCostGroup(payload.pricelistId, payload.costGroupDto)
);

const createCost$ = createEpicFromAsyncAction(createCost, (payload, { costClient }) =>
    costClient.createCost(payload.costGroupId, payload.createCostRequest)
);

const getCalculations$ = createEpicFromAsyncActionEmptyRequest(fetchCalculations, ({ calcClient }) =>
    calcClient.getAll()
);

const refreshCalculation$: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(x =>
            [
                createCostGroup.success,
                reorderCost.success,
                createCost.success,
                updateCostGroup.success,
                updateCalculation.success
            ].some(a => a.match(x))
        ),
        withLatestFrom(state$.pipe(map(state => state.calculation.calculation.pricelistId))),
        map(([, pricelistId]) => fetchCalculation.request(pricelistId))
    );

const getCalculationAfterCreationEpic$: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(createCalculation.success.match),
        withLatestFrom(state$.pipe(map(state => state.calculation.selectedCalculationPricelist))),
        map(([, pricelistId]) => fetchCalculation.request(pricelistId))
    );

const reorderCost$ = createEpicFromAsyncAction(reorderCost, (payload, { costClient }) =>
    costClient.reorderCost(payload.costId, payload.order)
);

const calculateCalculation$: RootEpic = (action$, state$, { calcClient }) =>
    action$.pipe(
        filter(calculateCalculation.request.match),
        withLatestFrom(state$.pipe(map(state => state.calculation.basePrice))),
        switchMap(([action, basePrice]) =>
            from(calcClient.calculate(action.payload.pricelistId, basePrice, action.payload.productId)).pipe(
                map(calculationResult => {
                    return calculateCalculation.success(calculationResult);
                }),
                catchError(err => of(pricePrgError(err)))
            )
        )
    );

const deleteCostGroup$ = createEpicFromAsyncAction(deleteCostGroup, (payload, { costGroupClient }) =>
    costGroupClient.deleteCostGroup(payload.costGroupId)
);

const updateCostGroup$ = createEpicFromAsyncAction(updateCostGroup, (payload, { costGroupClient }) =>
    costGroupClient.updateCostGroup(payload)
);

const deleteCost$ = createEpicFromAsyncAction(deleteCost, (payload, { costClient }) =>
    costClient.deleteCost(payload.costId)
);

const refreshCalculationAfterCostGroupDeletion$: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(x =>
            [
                deleteCostGroup.success,
                deleteCost.success,
                updateCost.success,
                createCostGroup.success,
                updateCostGroup.success
            ].some(a => a.match(x))
        ),
        withLatestFrom(state$.pipe(map(state => state.calculation.calculation.pricelistId))),
        map(([, pricelistId]) => fetchCalculation.request(pricelistId))
    );

const searchProducts$: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(searchProduct.request.match),
        withLatestFrom(state$.pipe(map(state => state.product.products))),
        map(([a, products]) =>
            searchProduct.success(
                Object.values(products)
                    .filter(
                        product =>
                            startsWith(a.payload ?? undefined, String(product.productId)) ||
                            includes(a.payload ?? undefined, product.name)
                    )
                    .map(product => ({
                        name: product.name,
                        productId: product.productId
                    }))
            )
        )
    );

const updateCost$ = createEpicFromAsyncAction(updateCost, (payload, { costClient }) =>
    costClient.updateCost(payload.costId.toString(), payload)
);

const fetchRounders$ = createEpicFromAsyncActionEmptyRequest(fetchRounders, ({ calcClient }) =>
    calcClient.getRounders()
);

const updateCalculation$: RootEpic = (action$, state$, { calcClient }) =>
    action$.pipe(
        filter(updateCalculation.request.match),
        withLatestFrom(state$.pipe(map(state => state.calculation.calculation))),
        switchMap(([action, calculation]) =>
            from(
                calcClient.update(calculation.calculationId.toString(), {
                    calculationId: calculation.calculationId,
                    name: action.payload.name,
                    rounder: action.payload.rounder
                })
            ).pipe(
                map(() => updateCalculation.success(action.payload)),
                catchError(err => of(pricePrgError(err)))
            )
        )
    );

const recalculatePrices$ = createEpicFromAsyncAction(
    recalculatePrices,
    (request, { calcClient }) => calcClient.recalculatePrices(request.pricelistId, request.fromDate),
    (request, pricesUpdated) => (pricesUpdated ? notifyPricesRecalculated(request.pricelistId) : null)
);

const fetchPricelistPeriods$ = createEpicFromAsyncAction(
    fetchPricelistPeriods,
    (request, { pricelistClient }) => pricelistClient.getPricelistsPeriodsForPricelists(request)
);

export default [
    fetchCalculation$,
    createCalculation$,
    fetchCostTypes$,
    createCostGroup$,
    refreshCalculation$,
    deleteCostGroup$,
    refreshCalculationAfterCostGroupDeletion$,
    reorderCost$,
    calculateCalculation$,
    createCost$,
    searchProducts$,
    deleteCost$,
    updateCost$,
    updateCostGroup$,
    getCalculationAfterCreationEpic$,
    fetchRounders$,
    updateCalculation$,
    recalculatePrices$,
    getCalculations$,
    fetchPricelistPeriods$
];
